/**
 * Daily Fact Component
 * Displays a beautiful, engaging daily fact
 * Designed to integrate seamlessly into the homepage
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Sparkles, 
  RefreshCw, 
  ExternalLink, 
  Clock,
  BookOpen,
  Share2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { factsService } from '@/services/factsService';
import type { DailyFact, DailyFactDisplayOptions } from '@/types/facts';
import { FACT_CATEGORY_COLORS } from '@/types/facts';

interface DailyFactProps {
  className?: string;
  options?: DailyFactDisplayOptions;
  onShare?: (fact: DailyFact) => void;
}

/**
 * Daily Fact Card Component
 * Shows today's fact with beautiful styling
 */
const DailyFactCard: React.FC<DailyFactProps> = ({ 
  className,
  options = {},
  onShare
}) => {
  const [fact, setFact] = useState<DailyFact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    showSource = true,
    showCategory = true,
    showImage = true,
    maxLength,
    className: optionsClassName
  } = options;

  /**
   * Load today's fact
   */
  const loadTodaysFact = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const todaysResult = await factsService.getTodaysFact();
      if (todaysResult.success && todaysResult.data) {
        setFact(todaysResult.data);
      } else {
        // Fallback to random fact
        const randomResult = await factsService.getRandomFact();
        if (randomResult.success && randomResult.data) {
          setFact(randomResult.data);
        } else {
          setError('Unable to load fact');
        }
      }
    } catch (err) {
      console.error('Failed to load daily fact:', err);
      setError('Unable to load today\'s heritage fact');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load a new random fact
   */
  const loadNewFact = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const randomResult = await factsService.getRandomFact();
      if (randomResult.success && randomResult.data) {
        setFact(randomResult.data);
      } else {
        setError('Unable to load a new fact');
      }
    } catch (err) {
      console.error('Failed to load new fact:', err);
      setError('Unable to load a new fact');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle sharing the fact
   */
  const handleShare = () => {
    if (fact && onShare) {
      onShare(fact);
    } else if (fact && navigator.share) {
      navigator.share({
        title: `Fact of the Day: ${fact.title}`,
        text: fact.fact_text,
        url: window.location.href
      });
    } else if (fact) {
      // Fallback to copying to clipboard
      const shareText = `${fact.title}\n\n${fact.fact_text}${fact.source ? `\n\nSource: ${fact.source}` : ''}`;
      navigator.clipboard.writeText(shareText);
    }
  };

  // Load fact on component mount
  useEffect(() => {
    loadTodaysFact();
  }, []);

  // Format fact text with length limit
  const formatFactText = (text: string): string => {
    if (!maxLength || text.length <= maxLength) {
      return text;
    }
    
    const truncated = text.slice(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    return lastSpace > 0 ? truncated.slice(0, lastSpace) + '...' : truncated + '...';
  };

  // Loading state
  if (loading) {
    return (
      <Card className={cn("relative overflow-hidden", className, optionsClassName)}>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-heritage animate-pulse" />
            <Badge variant="outline" className="animate-pulse">
              <div className="w-16 h-4 bg-gray-200 rounded"></div>
            </Badge>
          </div>
          
          <div className="space-y-3">
            <div className="w-3/4 h-6 bg-gray-200 rounded animate-pulse"></div>
            <div className="space-y-2">
              <div className="w-full h-4 bg-gray-100 rounded animate-pulse"></div>
              <div className="w-full h-4 bg-gray-100 rounded animate-pulse"></div>
              <div className="w-2/3 h-4 bg-gray-100 rounded animate-pulse"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error || !fact) {
    return (
      <Card className={cn("relative overflow-hidden border-red-200", className, optionsClassName)}>
        <CardContent className="p-6 text-center">
          <BookOpen className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-800 mb-2">
            Unable to Load Fact
          </h3>
          <p className="text-red-600 mb-4">
            {error || 'No facts available at the moment'}
          </p>
          <Button 
            variant="outline" 
            size="sm"
            onClick={loadTodaysFact}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn(
      "relative overflow-hidden bg-gradient-to-br from-heritage/5 to-heritage/10 border-heritage/20",
      className,
      optionsClassName
    )}>
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-heritage/5 rounded-full -mr-16 -mt-16"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-heritage/5 rounded-full -ml-12 -mb-12"></div>
      
      <CardContent className="p-6 relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-heritage" />
            <span className="text-sm font-medium text-heritage">
              Fact of the Day
            </span>
            <Clock className="w-4 h-4 text-muted-foreground" />
          </div>
          
          <div className="flex items-center gap-2">
            {showCategory && (
              <Badge 
                variant="secondary" 
                className={cn(
                  "text-xs",
                  FACT_CATEGORY_COLORS[fact.category] || 'bg-gray-100 text-gray-800'
                )}
              >
                {fact.category}
              </Badge>
            )}
          </div>
        </div>

        {/* Image (if available and enabled) */}
        {showImage && fact.image_url && (
          <div className="mb-4 rounded-lg overflow-hidden">
            <img 
              src={fact.image_url} 
              alt={fact.title}
              className="w-full h-48 object-cover"
              loading="lazy"
            />
          </div>
        )}

        {/* Fact Title */}
        <h3 className="text-xl font-bold text-foreground mb-3 leading-tight">
          {fact.title}
        </h3>

        {/* Fact Content */}
        <p className="text-muted-foreground leading-relaxed mb-4">
          {formatFactText(fact.fact_text)}
        </p>

        {/* Source and Actions */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-heritage/10">
          <div className="flex-1">
            {showSource && fact.source && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <BookOpen className="w-4 h-4" />
                <span>Source: {fact.source}</span>
                {fact.source_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-0 h-auto text-heritage hover:text-heritage/80"
                    onClick={() => window.open(fact.source_url, '_blank')}
                  >
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShare}
              className="text-muted-foreground hover:text-heritage"
              title="Share this fact"
            >
              <Share2 className="w-4 h-4" />
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={loadNewFact}
              disabled={loading}
              className="text-muted-foreground hover:text-heritage"
              title="Load another fact"
            >
              <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
            </Button>
          </div>
        </div>

        {/* Tags (if available) */}
        {fact.tags && fact.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {fact.tags.slice(0, 4).map((tag, index) => (
              <Badge 
                key={index} 
                variant="outline" 
                className="text-xs text-heritage/70 border-heritage/20"
              >
                #{tag}
              </Badge>
            ))}
            {fact.tags.length > 4 && (
              <Badge variant="outline" className="text-xs text-muted-foreground">
                +{fact.tags.length - 4} more
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Compact Daily Fact Component
 * A smaller version for sidebars or secondary areas
 */
export const CompactDailyFact: React.FC<DailyFactProps> = ({ 
  className,
  options = {}
}) => {
  return (
    <DailyFactCard 
      className={cn("max-w-sm", className)}
      options={{
        showImage: false,
        showSource: false,
        maxLength: 150,
        ...options
      }}
    />
  );
};

/**
 * Featured Daily Fact Component
 * Enhanced version for hero sections or feature areas
 */
export const FeaturedDailyFact: React.FC<DailyFactProps> = ({ 
  className,
  options = {},
  onShare
}) => {
  return (
    <DailyFactCard 
      className={cn("max-w-2xl mx-auto", className)}
      options={{
        showImage: true,
        showSource: true,
        showCategory: true,
        ...options
      }}
      onShare={onShare}
    />
  );
};

export default DailyFactCard;






